import { useMemo } from "react";
import { useTable } from "react-table";
import { v4 as uuidv4 } from "uuid";
import primaryComponents from "../../primaryComponents";
import RadioButtonV2 from "../../primaryComponents/RadioButtonV2";

const useSheduleDeliveryTable = ({
  items,
  selected,
  allSelected,
  someSelected,
  toggleItem,
  toggleAll,
  setScheduleData,
  updateScheduleField,
  register,
  setValue,
  watch,
  trigger,
  clearErrors,
  errors,
  control,
}) => {
  const columns = useMemo(
    () => [
      {
        Header: (
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <primaryComponents.Checkbox
              name="selectAll"
              id="selectAll"
              isChecked={allSelected}
              indeterminate={someSelected}
              onChange={toggleAll}
            />
            <span>Select All</span>
          </div>
        ),
        accessor: "is_active",
        Cell: ({ row }) => {
          const id = row.original.id;

          if (
            row.original.is_active === false &&
            row.original.supports_24_hours_delivery !== true
          ) {
            row.original.supports_24_hours_delivery = true;
            setValue(`supports24_${id}`, true);
          }
          const handleCheckboxChange = (checked) => {
            toggleItem(id, checked);
            setScheduleData((prev) =>
              prev.map((item) =>
                item.id === id
                  ? {
                      ...item,
                      is_active: checked,
                      delivery_start_time: checked
                        ? item.delivery_start_time
                        : null,
                      delivery_end_time: checked
                        ? item.delivery_end_time
                        : null,
                      supports_24_hours_delivery: checked
                        ? item.supports_24_hours_delivery
                        : true,
                    }
                  : item,
              ),
            );

            updateScheduleField(id, {
              is_active: checked,
              delivery_start_time: checked
                ? row.original.delivery_start_time
                : null,
              delivery_end_time: checked
                ? row.original.delivery_end_time
                : null,
              supports_24_hours_delivery: checked
                ? row.original.supports_24_hours_delivery
                : true,
            });

            if (!checked) {
              setValue(`startTime_${id}`, "");
              setValue(`endTime_${id}`, "");
              setValue(`supports24_${id}`, true);
              clearErrors([
                `startTime_${id}`,
                `endTime_${id}`,
                `supports24_${id}`,
              ]);
              updateScheduleField(id, {
                delivery_start_time: null,
                delivery_end_time: null,
                supports_24_hours_delivery: false,
              });
            }
          };

          return (
            <primaryComponents.Checkbox
              name={`select-${id}`}
              id={`select-${id}`}
              isChecked={!!selected[id]}
              onChange={handleCheckboxChange}
            />
          );
        },
      },
      { Header: "Delivery Days", accessor: "day_of_the_week" },
      {
        Header: "24hrs Delivery?",
        accessor: "supports_24_hours_delivery",
        Cell: ({ row }) => {
          const id = row.original.id;
          const uuid = uuidv4();
          const watchSupports24 = watch(`supports24_${id}`);

          const handleRadioChange = (value) => {
            if (!selected[id]) return;
            setScheduleData((prev) =>
              prev.map((item) =>
                item.id === id
                  ? {
                      ...item,
                      supports_24_hours_delivery: value,
                      delivery_start_time: value
                        ? null
                        : item.delivery_start_time,
                      delivery_end_time: value ? null : item.delivery_end_time,
                    }
                  : item,
              ),
            );

            updateScheduleField(id, {
              supports_24_hours_delivery: value,
              delivery_start_time: value ? null : null,
              delivery_end_time: value ? null : null,
            });

            if (value) {
              setValue(`startTime_${id}`, "");
              setValue(`endTime_${id}`, "");
            }

            clearErrors([`startTime_${id}`, `endTime_${id}`]);
          };

          return (
            <div
              className="d-flex align-items-center"
              style={{
                gap: "6px",
                borderRadius: "24px",
                border: "2px",
                borderStyle: "solid",
                borderColor: "#E1E1E1",
                padding: "5px 10px",
                width: "max-content",
                pointerEvents: !selected[id] ? "none" : "auto",
                opacity: !selected[id] ? 0.6 : 1,
                backgroundColor: !selected[id] ? "#F5F5F5" : "transparent",
              }}
            >
              <input
                type="hidden"
                {...register(`supports24_${id}`)}
                value={row.original.supports_24_hours_delivery}
              />
              <RadioButtonV2
                name={`yesNo-${uuid}`}
                id={`yes-${id}`}
                value={true}
                checked={
                  watchSupports24 === true ||
                  row.original.supports_24_hours_delivery === true
                }
                onChange={(value) => {
                  handleRadioChange(value);
                  setValue(`supports24_${id}`, value);
                  trigger([`startTime_${id}`, `endTime_${id}`]);
                }}
                label="Yes"
                disabled={!selected[id]}
              />
              <div
                style={{
                  width: "2px",
                  height: "20px",
                  backgroundColor: "#E1E1E1",
                }}
              />
              <RadioButtonV2
                name={`yesNo-${uuid}`}
                id={`no-${id}`}
                value={false}
                checked={
                  watchSupports24 === false ||
                  row.original.supports_24_hours_delivery === false
                }
                onChange={(value) => {
                  handleRadioChange(value);
                  setValue(`supports24_${id}`, value);
                  trigger([`startTime_${id}`, `endTime_${id}`]);
                }}
                label="No"
                disabled={!selected[id]}
              />
            </div>
          );
        },
      },
      { Header: "", accessor: "u" },
      {
        Header: "Time Slots",
        accessor: "time_slots",
        Cell: ({ row }) => {
          const id = row.original.id;
          const watchSupports24 = watch(`supports24_${id}`);

          const handleTimeChange = (field, value) => {
            setScheduleData((prev) =>
              prev.map((item) =>
                item.id === id ? { ...item, [field]: value } : item,
              ),
            );

            updateScheduleField(id, { [field]: value });
          };

          const is24hrs =
            watchSupports24 === true ||
            row.original.supports_24_hours_delivery === true;

          return (
            <div className="d-flex w-100" style={{ gap: "5px" }}>
              <primaryComponents.TimeInputV2
                name={`startTime_${id}`}
                control={control}
                register={register}
                value={row.original.delivery_start_time}
                label="Start Time"
                isDisabled={!selected[id] || is24hrs}
                className="w-100"
                errorMessage={errors?.[`startTime_${id}`]}
                onChange={({ target: { value } }) =>
                  handleTimeChange("delivery_start_time", value)
                }
                onKeyUp={() =>
                  errors?.[`startTime_${id}`] && trigger(`startTime_${id}`)
                }
              />
              <primaryComponents.TimeInputV2
                name={`endTime_${id}`}
                control={control}
                register={register}
                value={row.original.delivery_end_time}
                label="End Time"
                className="w-100"
                isDisabled={!selected[id] || is24hrs}
                errorMessage={errors?.[`endTime_${id}`]}
                onChange={({ target: { value } }) =>
                  handleTimeChange("delivery_end_time", value)
                }
                onKeyUp={() =>
                  errors?.[`endTime_${id}`] && trigger(`endTime_${id}`)
                }
              />
            </div>
          );
        },
      },
    ],
    [items, selected, allSelected, someSelected, control, watch, errors],
  );

  const instance = useTable({
    columns,
    data: items || [],
  });

  return { instance };
};

export default useSheduleDeliveryTable;
